import { SharedStoreState } from '@core/data-access/models/shared/shared-store.model';
import { TicketSendingOptionsActions } from '@core/data-access/store/shared/actions/ticket-sending-option.actions';
import { createReducer, on } from '@ngrx/store';
import { cloneDeep, isEmpty, omit, some, times, zipObject } from 'lodash';
import { GoogleMapsApiCacheActions } from './actions/google-maps-api-cache.actions';
import { SharedActions } from './shared.actions';

export const sharedReducerKey = 'shared';

const initialSharedStoreState: SharedStoreState = {
  selectedTicketSendingOptions: {},
  googleMapsApiCache: {
    addresses: {},
    companies: {},
    zipCodes: []
  }
};

export const sharedReducer = createReducer(
  initialSharedStoreState,
  on(
    TicketSendingOptionsActions.setTicketSendingOptions,
    (state, { selectedTicketSendingOption, addBookedTariffUuids, removeBookedTariffUuids }) => {
      if (isEmpty(selectedTicketSendingOption)) {
        return state;
      }

      const selectedTicketSendingOptions = omit(state.selectedTicketSendingOptions, removeBookedTariffUuids);
      const addedSelectedTicketSendingOptions = zipObject(
        addBookedTariffUuids,
        times(addBookedTariffUuids.length, () => selectedTicketSendingOption)
      );

      return {
        ...state,
        selectedTicketSendingOptions: {
          ...selectedTicketSendingOptions,
          ...addedSelectedTicketSendingOptions
        }
      };
    }
  ),
  on(TicketSendingOptionsActions.removeTicketSendingOptions, (state, { removeTariffUuids }) => ({
    ...state,
    selectedTicketSendingOptions: omit(state.selectedTicketSendingOptions, removeTariffUuids)
  })),
  on(GoogleMapsApiCacheActions.cacheAddress, (state, { id, placeDetails }) => {
    const addressExists = !isEmpty(state.googleMapsApiCache.addresses[id]);

    if (addressExists) {
      return state;
    }

    return {
      ...state,
      googleMapsApiCache: {
        ...state.googleMapsApiCache,
        addresses: {
          ...state.googleMapsApiCache.addresses,
          [id]: placeDetails
        }
      }
    };
  }),
  on(GoogleMapsApiCacheActions.cacheCompany, (state, { id, placeDetails }) => {
    const companyExists = !isEmpty(state.googleMapsApiCache.companies[id]);

    if (companyExists) {
      return state;
    }

    return {
      ...state,
      googleMapsApiCache: {
        ...state.googleMapsApiCache,
        companies: {
          ...state.googleMapsApiCache.companies,
          [id]: placeDetails
        }
      }
    };
  }),
  on(GoogleMapsApiCacheActions.cacheZipCodeState, (state, { zipCode, countryCode, cities, isValid }) => {
    const zipCodeExists = some(state.googleMapsApiCache.zipCodes, { zipCode, countryCode });

    if (zipCodeExists) {
      return state;
    }

    const clonedZipCodes = cloneDeep(state.googleMapsApiCache.zipCodes);

    clonedZipCodes.push({
      zipCode,
      countryCode,
      cities,
      isValid
    });

    return {
      ...state,
      googleMapsApiCache: {
        ...state.googleMapsApiCache,
        zipCodes: clonedZipCodes
      }
    };
  }),
  on(SharedActions.resetReducer, () => initialSharedStoreState)
);
